import type { AvailableTransition, CommentVisibility, TicketDetail } from '@servicetim/shared';
import { useExecuteTransition, usePriorities, useResolvedForm } from '@servicetim/ui/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Label,
  LoadingState,
  ProblemBanner,
  SchemaForm,
  type SchemaFormSection,
  Switch,
  sectionsFromResolvedForm,
  splitTicketValues,
  Textarea,
  ticketToFormValues,
  toast,
} from '@servicetim/ui/components';
import { useId, useMemo, useState } from 'react';
import { useAgentUserOptionLoader } from './api';
import { systemFieldsReadOnly } from './form-source';

/** The parts of a ticket the dialog needs (a detail or a list row with `display`). */
export type TransitionTicket = Pick<
  TicketDetail,
  | 'id'
  | 'key'
  | 'projectId'
  | 'summary'
  | 'description'
  | 'priorityId'
  | 'assigneeId'
  | 'labels'
  | 'dueAt'
  | 'fields'
> & { display?: Record<string, string> };

export interface TransitionDialogProps {
  ticket: TransitionTicket;
  /** The transition to run; `null` keeps the dialog closed. */
  transition: AvailableTransition | null;
  onOpenChange: (open: boolean) => void;
  onExecuted?: (ticket: TicketDetail) => void;
}

/**
 * Runs a transition: the transition form (`GET …/forms/{formId}/resolved`, when the transition
 * has one) plus an optional comment, then `POST …/transitions/{tid}`. Only `cf_` values travel
 * with a transition, so system fields placed on the form render read-only.
 */
export function TransitionDialog({
  ticket,
  transition,
  onOpenChange,
  onExecuted,
}: TransitionDialogProps) {
  return (
    <Dialog open={transition !== null} onOpenChange={onOpenChange}>
      <DialogContent size="lg" aria-describedby={undefined}>
        {transition ? (
          <TransitionBody
            ticket={ticket}
            transition={transition}
            onOpenChange={onOpenChange}
            onExecuted={onExecuted}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function TransitionBody({
  ticket,
  transition,
  onOpenChange,
  onExecuted,
}: TransitionDialogProps & { transition: AvailableTransition }) {
  const resolved = useResolvedForm(ticket.projectId, transition.formId ?? undefined);
  const priorities = usePriorities(transition.formId ? ticket.projectId : undefined);
  const loadUsers = useAgentUserOptionLoader();
  const pickers = useMemo(() => ({ users: loadUsers }), [loadUsers]);
  const execute = useExecuteTransition();
  const [comment, setComment] = useState('');
  const [visibility, setVisibility] = useState<CommentVisibility>('public');
  const commentId = useId();
  const internalId = useId();

  const sections = useMemo<SchemaFormSection[]>(() => {
    if (!transition.formId) return [];
    if (!resolved.data) return [];
    return systemFieldsReadOnly(
      sectionsFromResolvedForm(resolved.data, undefined, {
        priorities: priorities.data?.map((p) => ({ id: p.id, label: p.name, color: p.color })),
      }),
    );
  }, [transition.formId, resolved.data, priorities.data]);

  const submit = async (values: Record<string, unknown>) => {
    const body = comment.trim();
    const result = await execute.mutateAsync({
      id: ticket.id,
      transitionId: transition.id,
      fields: splitTicketValues(values).fields,
      comment: body.length > 0 ? { body, visibility } : undefined,
    });
    toast.success(`${ticket.key}: ${transition.name}`);
    onOpenChange(false);
    onExecuted?.(result);
  };

  const loadingForm = transition.formId !== null && resolved.isPending;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{transition.name}</DialogTitle>
        <DialogDescription>
          {ticket.key} · {ticket.summary}
        </DialogDescription>
      </DialogHeader>
      {resolved.error ? <ProblemBanner error={resolved.error} size="sm" /> : null}
      {loadingForm ? (
        <LoadingState rows={3} label="Loading the transition form…" />
      ) : (
        <SchemaForm
          sections={sections}
          defaultValues={ticketToFormValues(ticket)}
          display={ticket.display}
          onSubmit={submit}
          submitLabel={transition.name}
          onCancel={() => onOpenChange(false)}
          busy={execute.isPending}
          pickers={pickers}
          emptyValue="omit"
        >
          <div className="space-y-2 border-border border-t pt-4">
            <Label htmlFor={commentId}>Comment (optional)</Label>
            <Textarea
              id={commentId}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Add a note with this change…"
            />
            <div className="flex items-center gap-2">
              <Switch
                id={internalId}
                checked={visibility === 'internal'}
                onCheckedChange={(checked) => setVisibility(checked ? 'internal' : 'public')}
              />
              <Label htmlFor={internalId} className="font-normal">
                Internal note (hidden from requesters)
              </Label>
            </div>
          </div>
        </SchemaForm>
      )}
    </>
  );
}
